/**
 * Node visual para atribuir vendedor a negócio CRM no Flow Builder
 * Exibe configuração de seleção do deal e modo de atribuição (manual ou fila)
 * 
 * RELEASE: 1.0.0 - 2025-01-25 - Novo node CRM
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { UserCheck, Search, Variable, Users, Zap } from 'lucide-react';
import { FlowNodeData, CrmAssignSellerConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Labels para exibição do filtro de status
 */
const STATUS_LABELS: Record<string, string> = {
  'open': 'Em Aberto',
  'won': 'Concluído (Ganho)',
  'lost': 'Perdido',
  'all': 'Todos os Status',
};

/**
 * Labels para método de distribuição
 */
const METHOD_LABELS: Record<string, string> = {
  'round_robin': 'Rodízio',
  'least_busy': 'Menos Ocupado',
  'weighted': 'Ponderado',
};

/**
 * Componente visual do node Atribuir Vendedor CRM
 * Exibe estratégia de seleção do deal e modo de atribuição
 */
export function CrmAssignSellerNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as CrmAssignSellerConfig | undefined;
  
  // Verifica se node está configurado
  const isConfigured = config?.seller_assignment_mode && 
    (config.seller_assignment_mode === 'manual' ? config.seller_id : config.distribution_queue_id);
  
  // Determina texto da estratégia de seleção do deal
  const getSelectionLabel = () => {
    if (!config) return null;
    if (config.deal_selection === 'from_trigger') return 'Do gatilho CRM';
    if (config.deal_selection === 'from_variable') {
      return `{{${config.deal_variable_name || 'deal_id'}}}`;
    }
    return 'Mais recente do contato';
  };

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />
      
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-violet-400 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-violet-400 ring-2 ring-violet-400/30' : 'border-violet-300'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-violet-400/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-violet-400">
            <UserCheck className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm">{nodeData.config?.nickname || 'Atribuir Vendedor'}</span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Seleção do Deal */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  {config?.deal_selection === 'from_trigger' ? (
                    <Zap className="h-3 w-3" />
                  ) : config?.deal_selection === 'from_variable' ? (
                    <Variable className="h-3 w-3" />
                  ) : (
                    <Search className="h-3 w-3" />
                  )}
                  <span className="font-medium">Deal:</span>
                </div>
                <p className="pl-4 font-medium truncate" title={getSelectionLabel() || ''}>
                  {getSelectionLabel()}
                </p>
                
                {/* Filtro de status (apenas para most_recent) */}
                {config?.deal_selection !== 'from_variable' && config?.deal_status_filter && (
                  <p className="pl-4 text-muted-foreground">
                    Status: <span className="font-medium">{STATUS_LABELS[config.deal_status_filter] || config.deal_status_filter}</span>
                  </p>
                )}
              </div>

              {/* Modo de Atribuição */}
              <div className="pt-1 border-t border-border/50 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  {config?.seller_assignment_mode === 'distribution_queue' ? (
                    <Users className="h-3 w-3" />
                  ) : (
                    <UserCheck className="h-3 w-3" />
                  )}
                  <span className="font-medium">
                    {config?.seller_assignment_mode === 'distribution_queue' ? 'Fila:' : 'Vendedor:'}
                  </span>
                </div>
                <p className="pl-4 font-medium truncate" title={
                  config?.seller_assignment_mode === 'distribution_queue' 
                    ? config?.distribution_queue_name 
                    : config?.seller_name
                }>
                  {config?.seller_assignment_mode === 'distribution_queue' 
                    ? config?.distribution_queue_name 
                    : config?.seller_name}
                </p>
                
                {/* Método de distribuição */}
                {config?.seller_assignment_mode === 'distribution_queue' && config?.distribution_queue_method && (
                  <p className="pl-4 text-muted-foreground">
                    Método: <span className="font-medium">
                      {METHOD_LABELS[config.distribution_queue_method] || config.distribution_queue_method}
                    </span>
                  </p>
                )}
                
                {/* Indicador de redistribuição forçada */}
                {config?.seller_assignment_mode === 'distribution_queue' && config?.force_redistribute && (
                  <p className="pl-4 text-amber-500 dark:text-amber-400 text-[10px] font-medium">
                    ⚡ Redistribui já atribuídos
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-violet-400 !border-2 !border-white"
      />
    </>
  );
}
